import React, { useState, useEffect, useRef } from 'react';
import Toast from '@/components/common/Toast';
import {
  fetchGetEnterpriseMarketOnboarding,
  fetchSubmitEnterpriseMarketOnboarding,
} from '@/service/api';
import {
  CertStatus,
  EntFormData,
  EnterpriseCertForm,
  CertStatusResult,
} from '../components/certification';

const certStatusConfig: Record<CertStatus, { label: string; color: string; icon: string; desc: string }> = {
  unverified: {
    label: '未认证',
    color: 'bg-foreground-100 text-foreground-600',
    icon: 'ri-error-warning-line',
    desc: '',
  },
  pending: {
    label: '审核中',
    color: 'bg-accent-100 text-accent-700',
    icon: 'ri-time-line',
    desc: '您的企业认证申请正在审核中',
  },
  approved: {
    label: '已认证',
    color: 'bg-green-100 text-green-700',
    icon: 'ri-check-double-line',
    desc: '企业认证已通过',
  },
  rejected: {
    label: '已驳回',
    color: 'bg-red-100 text-red-600',
    icon: 'ri-close-circle-line',
    desc: '认证未通过，请根据驳回原因修改后重新提交',
  },
};

const mapApiStatusToCertStatus = (status?: string): CertStatus => {
  if (!status) return 'unverified';
  const s = status.toUpperCase();
  if (s === 'PENDING' || s === 'AUDITING' || s === 'SUBMITTED') return 'pending';
  if (s === 'APPROVED' || s === 'PASSED' || s === 'SUCCESS') return 'approved';
  if (s === 'REJECTED' || s === 'FAILED') return 'rejected';
  return 'unverified';
};

export interface EnterpriseCertSectionProps {
  className?: string;
  /** 是否已被其他认证锁定（如个人实名认证已通过/审核中） */
  isLockedByOther?: boolean;
  /** 外部控制显隐或展开表单 */
  showForm?: boolean;
  /** 展开表单状态改变回调 */
  onShowFormChange?: (show: boolean) => void;
  /** 认证状态更新回调 */
  onStatusChange?: (status: CertStatus) => void;
}

export const EnterpriseCertSection: React.FC<EnterpriseCertSectionProps> = ({
  className = '',
  isLockedByOther = false,
  showForm: externalShowForm,
  onShowFormChange,
  onStatusChange,
}) => {
  const [status, setStatus] = useState<CertStatus>('unverified');
  const [rejectReason, setRejectReason] = useState<string>('');
  const [submittedAt, setSubmittedAt] = useState<string>('');
  const [auditedAt, setAuditedAt] = useState<string>('');
  const [internalShowForm, setInternalShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const fetchedRef = useRef(false);

  const showForm = externalShowForm !== undefined ? externalShowForm : internalShowForm;
  const setShowForm = (val: boolean) => {
    setInternalShowForm(val);
    onShowFormChange?.(val);
  };

  const [entForm, setEntForm] = useState<EntFormData>({
    companyName: '',
    creditCode: '',
    legalPerson: '',
    legalId: '',
    contactName: '',
    contactPhone: '',
    businessLicenseAssetId: '',
    authorizationAssetId: '',
  });

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    fetchGetEnterpriseMarketOnboarding()
      .then((res: any) => {
        if (res && (res.code === undefined || res.code === 200 || res.code === 0)) {
          const cert = res.enterprise_certification;
          const residency = res.residency;
          const images = res.enterprise_images;
          const rawStatus = cert?.certification_status || residency?.status;

          if (cert?.submitted_at || residency?.submitted_at) {
            setSubmittedAt(cert?.submitted_at || residency?.submitted_at || '');
          }
          if (cert?.audited_at || residency?.approved_at) {
            setAuditedAt(cert?.audited_at || residency?.approved_at || '');
          }

          if (cert || residency) {
            const st = mapApiStatusToCertStatus(rawStatus);
            setStatus(st);
            onStatusChange?.(st);

            const rejectRemark = cert?.audit_remark || residency?.audit_remark;
            if (rejectRemark) {
              setRejectReason(rejectRemark);
            }
            if (cert) {
              setEntForm((prev) => ({
                ...prev,
                companyName: cert.enterprise_name || prev.companyName,
                creditCode: cert.unified_social_credit_code || prev.creditCode,
                legalPerson: cert.legal_representative_name || prev.legalPerson,
                legalId: cert.legal_representative_id_no || prev.legalId,
                legalRepresentativeIdType: (cert.legal_representative_id_type as Api.EnterpriseOnboarding.LegalRepresentativeIdType) || prev.legalRepresentativeIdType || 'ID_CARD',
                contactName: cert.operator_name || prev.contactName,
                contactPhone: cert.operator_phone || prev.contactPhone,
                businessLicenseAssetId: cert.business_license_asset_id || cert.business_license_asset?.asset_id || prev.businessLicenseAssetId,
                authorizationAssetId: cert.authorization_asset_id || cert.authorization_asset?.asset_id || prev.authorizationAssetId,
                businessLicenseAsset: cert.business_license_asset || prev.businessLicenseAsset,
                authorizationAsset: cert.authorization_asset || prev.authorizationAsset,
                enterpriseImages: images && images.length > 0 ? images.map((img: any) => ({
                  display_name: img.display_name,
                  sort_no: img.sort_no,
                  upload_asset_id: img.upload_asset_id || img.id,
                })) : prev.enterpriseImages,
              }));
            }
          }
        }
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData: Api.EnterpriseOnboarding.SubmitEnterpriseMarketOnboardingRequest = {
        enterprise_name: entForm.companyName,
        unified_social_credit_code: entForm.creditCode,
        legal_representative_name: entForm.legalPerson,
        legal_representative_id_no: entForm.legalId,
        legal_representative_id_type: entForm.legalRepresentativeIdType || 'ID_CARD',
        operator_name: entForm.contactName,
        operator_phone: entForm.contactPhone,
        operator_phone_verification_code: entForm.operatorPhoneVerificationCode || '',
        operator_phone_verification_token: entForm.operatorPhoneVerificationToken || undefined,
        business_license_asset_id: entForm.businessLicenseAssetId || undefined,
        authorization_asset_id: entForm.authorizationAssetId || undefined,
        enterprise_images: entForm.enterpriseImages && entForm.enterpriseImages.length > 0 ? entForm.enterpriseImages : undefined,
      };

      const res: any = await fetchSubmitEnterpriseMarketOnboarding(submitData);
      const code = res?.code;
      const isSuccess = res && (code === undefined || code === 200 || code === 0 || code === '200' || code === '0');
      if (isSuccess) {
        const cert = res.enterprise_certification;
        const residency = res.residency;
        const rawStatus = cert?.certification_status || residency?.status || 'PENDING';
        const newStatus = mapApiStatusToCertStatus(rawStatus);
        setStatus(newStatus);
        onStatusChange?.(newStatus);
        const rejectRemark = cert?.audit_remark || residency?.audit_remark;
        if (rejectRemark) {
          setRejectReason(rejectRemark);
        }
        setShowForm(false);
        const msg = res.message || res.msg || '企业认证与入驻申请提交成功！';
        setToast({ message: msg, type: 'success' });
      } else {
        const errMsg = res?.message || res?.msg || '提交企业认证失败';
        setToast({ message: errMsg, type: 'error' });
      }
    } catch (err: any) {
      const errMsg = err?.message || err?.msg || '提交企业认证失败，请稍后重试';
      setToast({ message: errMsg, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const statusConfig = certStatusConfig[status];

  return (
    <div className={`space-y-6 ${className}`}>
      {/* 模块标题栏 */}
      <div className="flex items-center justify-between pb-4 border-b border-foreground-100">
        <div>
          <h2 className="font-heading font-bold text-base text-foreground-950 flex items-center gap-2">
            <i className="ri-building-line text-accent-500 text-lg"></i>
            企业认证与入驻
          </h2>
          <p className="text-xs text-foreground-500 mt-1">验证企业营业执照及法人信息，解锁企业专属功能与对公结算</p>
        </div>
        <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusConfig.color}`}>
          {statusConfig.label}
        </span>
      </div>

      {/* 当未展开表单且为未认证或已驳回状态时展示功能概览与入口 */}
      {!showForm && (status === 'unverified' || status === 'rejected') && (
        <div className="p-5 rounded-xl bg-foreground-50/60 border border-foreground-100 space-y-4">
          {status === 'rejected' && rejectReason && (
            <div className="p-3 rounded-lg bg-red-50 text-red-600 text-xs flex items-start gap-2 border border-red-100">
              <i className="ri-error-warning-line text-sm mt-0.5 shrink-0"></i>
              <div>
                <span className="font-medium">驳回原因：</span>
                {rejectReason}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-foreground-600">
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-white border border-foreground-100/80 shadow-xs">
              <div className="w-7 h-7 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                <i className="ri-building-2-line text-sm"></i>
              </div>
              <div>
                <div className="font-semibold text-foreground-900 mb-0.5">企业合规认证</div>
                <div className="text-foreground-500 text-[11px]">校验营业执照与社会信用代码</div>
              </div>
            </div>
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-white border border-foreground-100/80 shadow-xs">
              <div className="w-7 h-7 rounded-md bg-accent-50 text-accent-600 flex items-center justify-center shrink-0">
                <i className="ri-bank-card-line text-sm"></i>
              </div>
              <div>
                <div className="font-semibold text-foreground-900 mb-0.5">企业对公结算</div>
                <div className="text-foreground-500 text-[11px]">支持企业开票与对公账户结算</div>
              </div>
            </div>
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-white border border-foreground-100/80 shadow-xs">
              <div className="w-7 h-7 rounded-md bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                <i className="ri-verified-badge-line text-sm"></i>
              </div>
              <div>
                <div className="font-semibold text-foreground-900 mb-0.5">机构专属标识</div>
                <div className="text-foreground-500 text-[11px]">获得官方认证蓝V与团队特权</div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-foreground-100/60">
            <span className="text-xs text-foreground-500">需准备：营业执照信用代码、法人及经办人信息</span>
            <button
              type="button"
              disabled={isLockedByOther}
              onClick={() => !isLockedByOther && setShowForm(true)}
              className={`px-5 py-2 text-xs font-medium rounded-lg transition-colors shrink-0 ${
                isLockedByOther
                  ? 'bg-foreground-100 text-foreground-400 cursor-not-allowed'
                  : 'bg-accent-500 hover:bg-accent-600 text-white shadow-xs'
              }`}
            >
              {status === 'rejected' ? '重新提交认证' : '开始企业认证'}
            </button>
          </div>
        </div>
      )}

      {/* 企业认证表单 */}
      {showForm && (status === 'unverified' || status === 'rejected') && (
        <EnterpriseCertForm
          enterpriseStatus={status}
          rejectReason={rejectReason}
          formData={entForm}
          submitting={submitting}
          onChange={(data) => setEntForm((prev) => ({ ...prev, ...data }))}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* 审核中或已通过状态展示 */}
      {(status === 'pending' || status === 'approved') && (
        <CertStatusResult
          activeCert="enterprise"
          individualStatus="unverified"
          enterpriseStatus={status}
          indForm={{
            realName: '',
            idNumber: '',
            contactName: '',
            contactPhone: '',
            idFrontAssetId: '',
            idBackAssetId: '',
          }}
          entForm={entForm}
          submittedAt={submittedAt}
          auditedAt={auditedAt}
        />
      )}

      {/* Toast 提示 */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default EnterpriseCertSection;
